import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Truck, MapPin, Calendar, DollarSign, ArrowRight, CheckCircle, AlertTriangle, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function buildOptimisticQuote(preview, formSnap) {
  if (!preview || !formSnap) return null;
  return {
    transport_type:      formSnap.transport_type,
    year:                parseInt(formSnap.year, 10),
    vehicle_make:        formSnap.make,
    vehicle_model:       formSnap.model,
    pickup_zip:          formSnap.pickup_zip,
    delivery_zip:        formSnap.delivery_zip,
    pickup_window_start: formSnap.pickup_window_start,
    pickup_window_end:   formSnap.pickup_window_end,
    risk_flags:          [],
    customer_price:      preview.price,
    distance_miles:      preview.distance_miles,
    metrics:             { pricing_source: preview.pricing_source },
    status:              'quoted',
    _optimistic:         true,
  };
}

export default function QuoteResultPage() {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const optimistic = buildOptimisticQuote(
    location.state?.preview,
    location.state?.formSnap,
  );

  const [quote, setQuote] = useState(optimistic);
  const [loading, setLoading] = useState(!optimistic);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const fetchQuote = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/quote/${loadId}`);
      if (!response.ok) throw new Error('Quote not found');
      const data = await response.json();
      setQuote(data);
    } catch (error) {
      if (!quote) {
        toast.error('Failed to load quote');
        navigate('/quote');
      }
    } finally {
      setLoading(false);
    }
  }, [loadId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchQuote();
  }, [loadId, fetchQuote]);

  const handlePayDeposit = async () => {
    setPaymentLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/deposit/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          load_id: loadId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) throw new Error('Failed to create payment session');
      
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!quote) return null;

  const depositAmount = quote.transport_type === 'open' ? 199 : 299;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6">
              <CheckCircle className="h-8 w-8 text-accent" />
            </div>
            <h1 className="font-outfit text-4xl font-semibold tracking-tight mb-4" data-testid="quote-result-title">
              Your Quote is Ready
            </h1>
            <p className="text-muted-foreground">
              Review your transport details and secure your booking with a refundable deposit.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Quote Card */}
            <div className="lg:col-span-2">
              <Card className="glass border-border">
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center justify-between">
                    <span>Transport Details</span>
                    <Badge variant={quote.transport_type === 'enclosed' ? 'default' : 'secondary'}>
                      {quote.transport_type === 'enclosed' ? 'Enclosed' : 'Open'} Carrier
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Vehicle */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Truck className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Vehicle</p>
                      <p className="font-medium" data-testid="quote-vehicle">
                        {quote.year} {quote.vehicle_make} {quote.vehicle_model}
                      </p>
                    </div>
                  </div>

                  {/* Route */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-accent" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">Route</p>
                      <div className="flex items-center gap-3">
                        <span className="font-medium" data-testid="quote-route">
                          {quote.pickup_zip}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{quote.delivery_zip}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1" data-testid="quote-distance">
                        {quote.distance_miles?.toFixed(0)} miles
                      </p>
                    </div>
                  </div>

                  {/* Pickup Window */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pickup Window</p>
                      <p className="font-medium" data-testid="quote-dates">
                        {new Date(quote.pickup_window_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} 
                        {' - '}
                        {new Date(quote.pickup_window_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>

                  {/* Risk Flags */}
                  {quote.risk_flags && quote.risk_flags.length > 0 && (
                    <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-destructive" />
                        <span className="text-sm font-medium">Special Considerations</span>
                      </div>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {quote.risk_flags.map((flag, i) => (
                          <li key={i}>"¢ {flag.replace(/_/g, ' ')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pricing Card */}
            <div className="lg:col-span-1">
              <Card className="glass border-border sticky top-24">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg">Price Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Total Price */}
                  <div className="text-center py-6 border-b border-border">
                    <p className="text-sm text-muted-foreground mb-1">Total Transport Cost</p>
                    <p className="font-outfit text-4xl font-bold" data-testid="quote-price">
                      ${quote.customer_price?.toFixed(2)}
                    </p>
                    {(() => {
                      const src = quote.metrics?.pricing_source;
                      if (!src) return null;
                      const map = {
                        cd_market:      { label: 'CD Market Price', cls: 'bg-green-500/10 text-green-400 border-green-500/20' },
                        price_estimate: { label: 'Live Estimate',   cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20'  },
                        standard:       { label: 'Standard Price',  cls: 'bg-muted text-muted-foreground border-border'       },
                      };
                      const { label, cls } = map[src] || map.standard;
                      return (
                        <span className={`inline-flex items-center gap-1 mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
                          <TrendingUp className="h-2.5 w-2.5" />
                          {label}
                        </span>
                      );
                    })()}
                  </div>

                  {/* Deposit */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-sm text-muted-foreground">Refundable Deposit</span>
                      <span className="font-semibold" data-testid="quote-deposit">${depositAmount}</span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-sm text-muted-foreground">Due at Pickup</span>
                      <span className="font-semibold">${(quote.customer_price - depositAmount).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 py-4 border-t border-border">
                    {[
                      { icon: Shield, text: 'Full Insurance Coverage' },
                      { icon: CheckCircle, text: 'Verified Carriers Only' },
                      { icon: DollarSign, text: 'No Hidden Fees' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <item.icon className="h-4 w-4 text-accent" />
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button 
                    onClick={handlePayDeposit}
                    disabled={paymentLoading || quote.status !== 'quoted'}
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full py-6"
                    data-testid="pay-deposit-btn"
                  >
                    {paymentLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : quote.status === 'deposit_paid' ? (
                      'Deposit Paid'
                    ) : (
                      <>
                        Pay ${depositAmount} Deposit
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Fully refundable until carrier dispatch is confirmed.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

